import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Typography, Stack, styled, Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { PricingSheetRegion } from '../../types/Region';
import { I18nKeys } from '../../constants/I18nKeys';
import { openDialog } from '../../ducks/dialogSlice';
import { Dialogs } from '../../constants/Dialogs';
import { PricingSheet } from '../../types/PricingSheet';
import { getPricingSheetDefaultLabel } from '../../utils/pricingSheetUtils';
import { PricingSidingUpchargeHeader } from '../PricingSidingUpchargeHeader';
import { PricingBaseHiddenPriceHeader } from './PricingBaseHiddenPriceHeader';
import { PriceByQuantityRadioGroup } from './PriceByQuantityRadioGroup';
import { SalesViewUILayout } from '../../constants/Viewer';
import { SizeBasedCategoryKey } from '../../constants/ClientUpdateCategoryKey';

const Header = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  cursor: 'pointer',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Subheader = styled(Typography)({
  fontSize: '14px',
  color: 'rgba(0, 0, 0, 0.60)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

type Props = {
  selectedPricingSheet: PricingSheet | undefined;
  pricingSheets: PricingSheet[];
  regions: PricingSheetRegion[];
};

export const PricingSheetHeader: React.FC<Props> = ({ selectedPricingSheet, pricingSheets, regions }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [showEditIcon, setShowEditIcon] = React.useState(false);

  const secondaryMenuItem = useAppSelector((state) => state?.menu.secondaryMenuItem);
  const { selectedPricingTabId: pricingTab = '' } = useAppSelector((state) => state?.viewer);

  const { id: selectedPricingSheetId, priceSetLabel, attributes = [] } = selectedPricingSheet || {};
  const sheetName =
    priceSetLabel ||
    getPricingSheetDefaultLabel(selectedPricingSheet, pricingTab, SalesViewUILayout.Horizon, secondaryMenuItem);
  const attributesLabel =
    attributes?.reduce((label, attr) => `${!label || !attr.label ? label : `${label}, `}${attr.label || ''}`, '') || '';

  return (
    <Box mb="12px">
      <Box sx={{ overflow: 'hidden' }}>
        <Stack spacing="8px">
          <Stack>
            {selectedPricingSheetId && (
              <Box
                overflow="visible"
                display="flex"
                alignItems="center"
                onMouseEnter={() => setShowEditIcon(true)}
                onMouseLeave={() => setShowEditIcon(false)}
              >
                <Header
                  onClick={() => {
                    dispatch(openDialog({ dialog: Dialogs.PricingSheetName }));
                  }}
                  noWrap
                >
                  {sheetName ||
                    t(I18nKeys.PricingBaseAccordionPricingSheetTitle, { pricingSheetId: selectedPricingSheetId })}
                </Header>
                <IconButton
                  sx={{ height: '24px', visibility: showEditIcon ? 'visible' : 'hidden' }}
                  disableRipple
                  onClick={() => {
                    dispatch(openDialog({ dialog: Dialogs.PricingSheetName }));
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            )}{' '}
            {attributesLabel && !sheetName && <Subheader noWrap>{attributesLabel}</Subheader>}
          </Stack>
          <PricingBaseHiddenPriceHeader regions={regions} />
          <PricingSidingUpchargeHeader selectedPricingSheetId={selectedPricingSheetId} pricingSheets={pricingSheets} />
        </Stack>
        <PriceByQuantityRadioGroup selectedPricingSheet={selectedPricingSheet} regions={regions} />
      </Box>
      {selectedPricingSheet &&
        (
          [SizeBasedCategoryKey.EndWalls, SizeBasedCategoryKey.SideWalls, SizeBasedCategoryKey.Siding] as string[]
        ).includes(selectedPricingSheet.category || '') && (
          <Divider sx={{ marginLeft: '-24px', marginRight: '-24px' }} />
        )}
    </Box>
  );
};
